import React, { useMemo } from "react";
import { DataGrid } from "@mui/x-data-grid";
import { Tooltip } from "@mui/material";
import moment from "moment";
import LinkRoundedIcon from "@mui/icons-material/LinkRounded";
import CategoryRoundedIcon from "@mui/icons-material/CategoryRounded";
import TagRoundedIcon from "@mui/icons-material/TagRounded";
import NotesRoundedIcon from "@mui/icons-material/NotesRounded";
import DonutLargeRoundedIcon from "@mui/icons-material/DonutLargeRounded";
import FlagRoundedIcon from "@mui/icons-material/FlagRounded";
import DirectionsRunRoundedIcon from "@mui/icons-material/DirectionsRunRounded";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import EventRoundedIcon from "@mui/icons-material/EventRounded";
import UpdateRoundedIcon from "@mui/icons-material/UpdateRounded";
import InboxRoundedIcon from "@mui/icons-material/InboxRounded";
import DonutSmallRoundedIcon from "@mui/icons-material/DonutSmallRounded";
import { Glyph, PersonAvatar, getPriorityMeta, getStatusMeta, getTypeMeta } from "../Modal/TaskWorkFlowParts";
import { useUserPhotos } from "../../utilities/userPhotos";

/* column header: a quiet glyph beside the label */
const headerWith = (Icon, label) => () => (
  <span className="il-th">
    <Icon />
    {label}
  </span>
);

const EmptyOverlay = ({ title = "No tasks here yet", hint = "Try another search or sprint, or create a new task." }) => (
  <div className="il-empty">
    <span className="il-empty__icon">
      <InboxRoundedIcon />
    </span>
    <strong>{title}</strong>
    <span>{hint}</span>
  </div>
);

/**
 * The tasks table used by every sprint section. It only draws: loading,
 * fetching and the issue dialog belong to the page around it.
 */
const AllIssuesTable = ({ issues = [], loading = false, onOpenIssue = () => {}, showSprint = true, pageSize = 10, emptyTitle, emptyHint }) => {
  /* an issue only stores the assignee's name and id, so the photo is looked up */
  const photoOf = useUserPhotos();

  const rows = useMemo(() => (Array.isArray(issues) ? issues : []).map((issue) => ({ ...issue, id: issue._id })), [issues]);

  const columns = [
    {
      field: "issueType",
      headerName: "Type",
      width: 112,
      renderHeader: headerWith(CategoryRoundedIcon, "Type"),
      renderCell: ({ value }) => {
        const meta = getTypeMeta(value);
        return (
          <span className={`il-type pj-tone--${meta.tone}`}>
            <Glyph meta={meta} />
            {value || "—"}
          </span>
        );
      },
    },
    {
      field: "name",
      headerName: "Key",
      width: 104,
      renderHeader: headerWith(TagRoundedIcon, "Key"),
      renderCell: ({ value }) => (
        <span className="il-key">
          {value}
          <LinkRoundedIcon />
        </span>
      ),
    },
    {
      field: "summary",
      headerName: "Summary",
      flex: 1,
      minWidth: 220,
      renderHeader: headerWith(NotesRoundedIcon, "Summary"),
      renderCell: ({ value }) => (
        <span className="il-summary" title={value}>
          {value}
        </span>
      ),
    },
    {
      field: "status",
      headerName: "Status",
      width: 152,
      renderHeader: headerWith(DonutLargeRoundedIcon, "Status"),
      renderCell: ({ value }) => {
        const meta = getStatusMeta(value);
        return (
          <span className={`il-status pj-tone--${meta.tone}`}>
            <Glyph meta={meta} />
            {value || "—"}
          </span>
        );
      },
    },
    {
      field: "priority",
      headerName: "Priority",
      width: 112,
      renderHeader: headerWith(FlagRoundedIcon, "Priority"),
      renderCell: ({ value }) => {
        const meta = getPriorityMeta(value);
        return (
          <span className={`il-priority pj-tone--${meta.tone}`}>
            <Glyph meta={meta} />
            {value || "Medium"}
          </span>
        );
      },
    },
    {
      field: "storyPointEstimate",
      headerName: "Points",
      width: 90,
      type: "number",
      align: "left",
      headerAlign: "left",
      renderHeader: headerWith(DonutSmallRoundedIcon, "Points"),
      renderCell: ({ value }) => <span className="il-points">{Number(value) || 0}</span>,
    },
    {
      field: "sprint",
      headerName: "Sprint",
      width: 150,
      renderHeader: headerWith(DirectionsRunRoundedIcon, "Sprint"),
      valueGetter: (value, row) => row?.sprintdetails?.name || "Backlog",
      renderCell: ({ row }) => {
        const name = row?.sprintdetails?.name;
        return (
          <span className={`il-sprint ${name ? "is-sprint" : ""}`} title={name || "Backlog"}>
            <span className="il-sprint__dot" />
            <span className="il-sprint__name">{name || "Backlog"}</span>
          </span>
        );
      },
    },
    {
      field: "assignee",
      headerName: "Assignee",
      width: 180,
      renderHeader: headerWith(PersonRoundedIcon, "Assignee"),
      valueGetter: (value, row) => row?.assignee?.[0]?.employeeName || row?.assignee?.[0]?.employeeEmail || "",
      renderCell: ({ value, row }) => (
        <span className={`il-person ${value ? "" : "is-empty"}`}>
          <PersonAvatar name={value} src={photoOf(row?.assignee?.[0])} size={26} />
          <span className="il-person__name">{value || "Unassigned"}</span>
        </span>
      ),
    },
    {
      field: "createdAt",
      headerName: "Created",
      width: 132,
      renderHeader: headerWith(EventRoundedIcon, "Created"),
      renderCell: ({ value }) => (
        <Tooltip title={moment(value).format("DD MMM YYYY, HH:mm")}>
          <span className="il-date">{moment(value).format("DD MMM YYYY")}</span>
        </Tooltip>
      ),
    },
    {
      field: "updatedAt",
      headerName: "Updated",
      width: 124,
      renderHeader: headerWith(UpdateRoundedIcon, "Updated"),
      renderCell: ({ value }) => (
        <Tooltip title={moment(value).format("DD MMM YYYY, HH:mm")}>
          <span className="il-date">{moment(value).fromNow()}</span>
        </Tooltip>
      ),
    },
  ];

  /* inside a sprint's own card the sprint is the heading, and Created gives way
     so the table fits the card without scrolling sideways */
  const visibleColumns = showSprint ? columns : columns.filter((column) => column.field !== "sprint" && column.field !== "createdAt");
  // a short list needs no pager; the footer only appears once there is a second page
  const paged = rows.length > pageSize;

  return (
    <DataGrid
      className={`il-grid ${paged ? "" : "is-unpaged"}`}
      rows={rows}
      columns={visibleColumns}
      autoHeight
      rowHeight={54}
      columnHeaderHeight={46}
      disableRowSelectionOnClick
      loading={loading}
      onCellClick={(params) => onOpenIssue(params.row)}
      initialState={{ pagination: { paginationModel: { pageSize } } }}
      pageSizeOptions={[10, 25, 50, 100]}
      hideFooter={!paged}
      slots={{ noRowsOverlay: EmptyOverlay, noResultsOverlay: EmptyOverlay }}
      slotProps={{ noRowsOverlay: { title: emptyTitle, hint: emptyHint }, noResultsOverlay: { title: emptyTitle, hint: emptyHint } }}
      sx={{ "--DataGrid-overlayHeight": rows.length ? "280px" : "180px" }}
    />
  );
};

export default AllIssuesTable;
